import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowUpRight, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BUSINESS_EMAIL = "Thebridgecollectivellc@gmail.com";

const SERVICE_OPTIONS = [
  "Corporate Security",
  "Retail Security",
  "Executive Protection",
  "VIP Protection",
  "Bodyguard Services",
  "Religious Institution & Church Security",
  "Event Security",
  "Private Security Consulting",
];

export default function InquiryForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    service_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Please complete name, email, and a brief message (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        service_type: form.service_type || null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Inquiry received. Our team will reply by email shortly.");
      setForm({
        full_name: "",
        email: "",
        organization: "",
        service_type: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Submission failed. Please email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiry"
      data-testid="inquiry-section"
      className="relative py-24 sm:py-32 bg-[#070707] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column */}
          <div className="lg:col-span-5 reveal">
            <span className="overline">Confidential Contact</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
              Discuss your security needs{" "}
              <span className="text-zinc-500">confidentially.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              Share a few details and our team will reply by email. All
              inquiries are handled privately and reviewed by a senior member of
              our protection group.
            </p>

            <div className="mt-10 space-y-6">
              <a
                href={`mailto:${BUSINESS_EMAIL}?subject=The%20Bridge%20Collective%20Inquiry`}
                data-testid="email-link"
                className="group flex items-start gap-4 border border-white/10 bg-[#0c0c0c] p-5 hover:border-[#144A33] transition-colors"
              >
                <Mail
                  size={20}
                  className="text-[#1A5C40] mt-1"
                  strokeWidth={1.4}
                />
                <div>
                  <div className="overline">Direct Email</div>
                  <div className="mt-1 font-display text-white text-base sm:text-lg break-all group-hover:text-white">
                    {BUSINESS_EMAIL}
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="ml-auto text-zinc-500 group-hover:text-white transition-colors"
                  strokeWidth={1.4}
                />
              </a>

              <div className="flex items-start gap-4 text-sm text-zinc-400">
                <Lock
                  size={18}
                  className="text-[#1A5C40] mt-0.5"
                  strokeWidth={1.4}
                />
                <span>
                  We do not publish phone numbers, addresses, or live chat by
                  design — discretion begins at first contact.
                </span>
              </div>

              <div className="text-xs text-zinc-500 tracking-[0.18em] uppercase">
                Serving DMV · Baltimore · Maryland
              </div>
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={submit}
              data-testid="inquiry-form"
              className="border border-white/10 bg-[#0c0c0c] p-8 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-3" htmlFor="full_name">
                    Full Name *
                  </label>
                  <input
                    id="full_name"
                    data-testid="form-full-name"
                    className="input-luxe"
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3" htmlFor="email">
                    Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    className="input-luxe"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@organization.com"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3" htmlFor="organization">
                    Organization
                  </label>
                  <input
                    id="organization"
                    data-testid="form-organization"
                    className="input-luxe"
                    value={form.organization}
                    onChange={update("organization")}
                    placeholder="Company, family office, institution"
                  />
                </div>
                <div>
                  <label className="overline block mb-3" htmlFor="service_type">
                    Service of Interest
                  </label>
                  <select
                    id="service_type"
                    data-testid="form-service-type"
                    className="input-luxe appearance-none cursor-pointer"
                    value={form.service_type}
                    onChange={update("service_type")}
                  >
                    <option value="" className="bg-[#0c0c0c]">
                      Select one
                    </option>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-[#0c0c0c]">
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="overline block mb-3" htmlFor="message">
                  Brief Overview *
                </label>
                <textarea
                  id="message"
                  data-testid="form-message"
                  className="input-luxe min-h-[140px] resize-y"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Briefly describe the engagement, environment, dates, and any considerations."
                  required
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
                  By submitting, you consent to be contacted by email regarding
                  your inquiry. Information shared is held in confidence.
                </p>
                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Inquiry"}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
              </div>

              {submitted && (
                <div
                  data-testid="form-success"
                  className="mt-6 text-sm text-[#1A5C40] tracking-wide"
                >
                  Inquiry received. A member of our team will respond by email.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
